import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Badge, Box, Button, Center, Checkbox, Collapse, Group, Loader, NavLink, NumberInput, Paper, PasswordInput, Select, SimpleGrid, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type ConnectionSource, type ConnectionsView, PORTAL_THROTTLE_BOUNDS, PORTAL_THROTTLE_DEFAULTS, PORTAL_THROTTLE_RECOMMENDED, type PortalConnection, type PortalThrottle, type SmtpSecurity,
} from '@pst/shared';
import { ApiError, type ConnectionGroup, connectionsApi, portalJobsApi } from '../api';
import { FETCH_PORTALS, PORTAL_ALLOW, PORTAL_NIGHT, PORTAL_THROTTLE_FIELDS, formatDateTime } from '../format';
import { Section } from '../ui/Section';
import { PortalRound } from './PortalRound';

const SOURCE: Record<ConnectionSource, { label: string; color: string }> = {
  pst: { label: 'in PST eingestellt', color: 'green' },
  env: { label: 'aus der .env', color: 'gray' },
  none: { label: 'nicht eingerichtet', color: 'orange' },
};

const SECURITY: Record<SmtpSecurity, string> = {
  starttls: 'STARTTLS (meist Port 587)',
  tls: 'TLS von Anfang an (meist Port 465)',
  none: 'unverschlüsselt (nur im eigenen Netz)',
};

const message = (e: unknown): string => (e instanceof ApiError || e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');

/**
 * Einstellungen › Verbindungen: the mail server for invitations and password
 * links, the access to Destatis GENESIS-Online for the building price index,
 * the key for the Claude API (ADR 0022), the portals PST fetches from
 * (ADR 0023) and ntfy. Set here, they win over the .env; passwords and tokens can be set or
 * removed, never read back.
 */
type Area = 'mail' | 'ntfy' | 'genesis' | 'anthropic' | PortalConnection['id'];
interface AreaItem { id: Area; label: string; state: { label: string; color: string } }

const sourceState = (source: ConnectionSource): AreaItem['state'] => SOURCE[source];
const portalState = (p: PortalConnection): AreaItem['state'] =>
  p.stoppedAt ? { label: 'gestoppt', color: 'red' }
    : !p.passwordSet ? { label: 'nicht eingerichtet', color: 'orange' }
    : p.probeOk ? { label: p.enabled ? `bereit, ${PORTAL_ALLOW.on}` : 'bereit', color: 'green' }
    : p.probeOk === false ? { label: 'Probe gescheitert', color: 'red' }
    : { label: 'Probe ausstehend', color: 'gray' };

/** The areas of the page in three groups, each with its state at a glance. */
function areaGroups(v: ConnectionsView): { title: string; items: AreaItem[] }[] {
  return [
    { title: 'Benachrichtigungen', items: [
      { id: 'mail', label: 'E-Mail-Versand', state: sourceState(v.mail.source) },
      { id: 'ntfy', label: 'ntfy', state: sourceState(v.ntfy.source) },
    ] },
    { title: 'Dienste', items: [
      { id: 'genesis', label: 'Destatis GENESIS-Online', state: sourceState(v.genesis.source) },
      { id: 'anthropic', label: 'Claude-API', state: sourceState(v.anthropic.source) },
    ] },
    { title: 'Portale für Abzüge', items: v.portals.map((p) => ({ id: p.id, label: FETCH_PORTALS[p.id].label, state: portalState(p) })) },
  ];
}

export function ConnectionsPanel(): JSX.Element {
  const view = useQuery({ queryKey: ['connections'], queryFn: connectionsApi.get });
  const [area, setArea] = useState<Area>('mail');
  const phone = useMediaQuery('(max-width: 48em)');
  if (view.isPending) return <Center h={120}><Loader size="sm" /></Center>;
  if (view.isError) return <Alert color="red">{message(view.error)}</Alert>;
  const v = view.data;
  const groups = areaGroups(v);
  const portal = v.portals.find((p) => p.id === area);
  const content = area === 'mail' ? <MailSection key={`mail-${v.mail.updatedAt ?? v.mail.source}`} view={v} />
    : area === 'ntfy' ? <NtfySection key={`ntfy-${v.ntfy.updatedAt ?? 'neu'}`} view={v} />
    : area === 'genesis' ? <GenesisSection key={`genesis-${v.genesis.updatedAt ?? v.genesis.source}`} view={v} />
    : area === 'anthropic' ? <AnthropicSection key={`anthropic-${v.anthropic.updatedAt ?? v.anthropic.source}`} view={v} />
    : portal ? <PortalSection key={`portal-${portal.id}-${portal.updatedAt ?? 'neu'}`} portal={portal} /> : null;
  const nav = phone ? (
    <Select aria-label="Verbindung" value={area} onChange={(a) => a && setArea(a as Area)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
      data={groups.map((g) => ({ group: g.title, items: g.items.map((i) => ({ value: i.id, label: `${i.label} – ${i.state.label}` })) }))} />
  ) : (
    <Paper withBorder radius="md" p="xs" w={250} style={{ flexShrink: 0, alignSelf: 'flex-start' }}>
      <Stack gap="sm">
        {groups.map((g) => (
          <Stack key={g.title} gap={2}>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="xs">{g.title}</Text>
            {g.items.map((i) => (
              <NavLink key={i.id} active={area === i.id} onClick={() => setArea(i.id)} label={i.label}
                description={<Group gap={6} wrap="nowrap"><Box w={8} h={8} style={{ borderRadius: 4, background: `var(--mantine-color-${i.state.color}-6)`, flexShrink: 0 }} /><span>{i.state.label}</span></Group>} />
            ))}
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
  return (
    <Stack gap="md" maw={1180}>
      <Group align="flex-start" gap="md" wrap="nowrap" style={phone ? { flexDirection: 'column', alignItems: 'stretch' } : undefined}>
        {nav}
        <Stack gap="md" style={{ flex: 1, minWidth: 0 }}>{content}</Stack>
      </Group>
      <Text size="xs" c="dimmed">
        Kennwörter, Tokens und Schlüssel speichert PST verschlüsselt und gibt sie nie an den Browser zurück. Jede Änderung und jede Prüfung steht ohne die Werte im Systemprotokoll.
        Wird PST_SESSION_SECRET geändert, müssen sie neu eingegeben werden.
      </Text>
    </Stack>
  );
}

function SourceLine({ source, updatedAt }: { source: ConnectionSource; updatedAt: string | null }): JSX.Element {
  return (
    <Group gap="xs">
      <Badge variant="light" color={SOURCE[source].color}>{SOURCE[source].label}</Badge>
      {updatedAt && <Text size="xs" c="dimmed">geändert {formatDateTime(updatedAt)}</Text>}
    </Group>
  );
}

function useConnectionActions(group: ConnectionGroup) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const test = useMutation({ mutationFn: () => connectionsApi.test(group) });
  const reset = useMutation({ mutationFn: () => connectionsApi.reset(group), onSettled: refresh });
  return { refresh, test, reset };
}

function TestResult({ result, error }: { result?: { ok: boolean; message: string }; error: unknown }): JSX.Element | null {
  if (error) return <Alert color="red" py={6}>{message(error)}</Alert>;
  if (!result) return null;
  return <Alert color={result.ok ? 'green' : 'red'} py={6}>{result.message}</Alert>;
}

function MailSection({ view }: { view: ConnectionsView }): JSX.Element {
  const m = view.mail;
  const [host, setHost] = useState(m.host);
  const [port, setPort] = useState<number | string>(m.port);
  const [security, setSecurity] = useState<SmtpSecurity>(m.security);
  const [user, setUser] = useState(m.user);
  const [from, setFrom] = useState(m.from);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const { refresh, test, reset } = useConnectionActions('mail');
  const save = useMutation({
    mutationFn: () => connectionsApi.saveMail({
      host: host.trim(), port: Number(port), security, user: user.trim(), from: from.trim(),
      ...(clearPassword ? { password: '' } : password ? { password } : {}),
    }),
    onSuccess: () => { setPassword(''); setClearPassword(false); },
    onSettled: refresh,
  });
  const portOk = Number.isInteger(Number(port)) && Number(port) >= 1 && Number(port) <= 65535;
  return (
    <Section title="E-Mail-Versand" description="Der Mailserver des Büros, über den PST Einladungen und Links zum Zurücksetzen des Kennworts verschickt. Ohne ihn zeigt PST den Link zum Weitergeben von Hand."
      actions={<SourceLine source={m.source} updatedAt={m.updatedAt} />}>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <TextInput label="Server" placeholder="smtp.example.org" value={host} onChange={(e) => setHost(e.currentTarget.value)} />
          <NumberInput label="Port" min={1} max={65535} allowDecimal={false} value={port} onChange={setPort} error={portOk ? undefined : '1 bis 65535'} />
          <Select label="Verschlüsselung" data={Object.entries(SECURITY).map(([value, label]) => ({ value, label }))} value={security} onChange={(v) => v && setSecurity(v as SmtpSecurity)} allowDeselect={false} comboboxProps={{ withinPortal: true }} />
        </SimpleGrid>
        {security === 'none' && <Alert color="orange" py={6}>Unverschlüsselt gehen Kennwort und Links im Klartext zum Mailserver. Nur wählen, wenn der Server im selben, geschützten Netz steht.</Alert>}
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <TextInput label="Benutzername" autoComplete="off" value={user} onChange={(e) => setUser(e.currentTarget.value)} />
          <Stack gap={2}>
            <PasswordInput label="Kennwort" autoComplete="new-password" disabled={clearPassword}
              placeholder={m.passwordSet ? 'gesetzt – leer lassen zum Behalten' : 'nicht gesetzt'} value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
            {m.passwordSet && <Checkbox size="xs" label="Kennwort entfernen" checked={clearPassword} onChange={(e) => setClearPassword(e.currentTarget.checked)} />}
            {m.passwordSet && <Text size="xs" c="dimmed">bei geändertem Server, Port, Benutzer oder Verschlüsselung neu eingeben</Text>}
          </Stack>
          <TextInput label="Absender" description="leer = Benutzername" placeholder="PST <pst@example.org>" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
        </SimpleGrid>
        {save.isError && <Alert color="red" py={6}>{message(save.error)}</Alert>}
        {reset.isError && <Alert color="red" py={6}>{message(reset.error)}</Alert>}
        <TestResult result={test.data} error={test.error} />
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate()} loading={save.isPending} disabled={!host.trim() || !portOk}>Speichern</Button>
          <Button size="xs" variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={m.source === 'none'} title="schickt eine Testnachricht an die eigene Adresse">Testnachricht an mich</Button>
          {m.source === 'pst' && <Button size="xs" variant="subtle" color="gray" onClick={() => reset.mutate()} loading={reset.isPending}>Auf .env zurücksetzen</Button>}
        </Group>
        {m.source === 'env' && <Text size="xs" c="dimmed">Beim Speichern übernimmt PST die Werte; ein Kennwort aus der .env bleibt, solange kein neues eingegeben wird.</Text>}
      </Stack>
    </Section>
  );
}

function GenesisSection({ view }: { view: ConnectionsView }): JSX.Element {
  const g = view.genesis;
  const [user, setUser] = useState(g.user);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState('');
  const [clear, setClear] = useState<{ password: boolean; token: boolean }>({ password: false, token: false });
  const { refresh, test, reset } = useConnectionActions('genesis');
  const save = useMutation({
    mutationFn: () => connectionsApi.saveGenesis({
      user: user.trim(),
      ...(clear.password ? { password: '' } : password ? { password } : {}),
      ...(clear.token ? { token: '' } : token ? { token } : {}),
    }),
    onSuccess: () => { setPassword(''); setToken(''); setClear({ password: false, token: false }); },
    onSettled: refresh,
  });
  return (
    <Section title="Destatis GENESIS-Online" description="Zugang für den Baupreisindex: PST holt die Tabellen einmal am Tag selbst. Ein kostenloses Konto gibt es bei Destatis; entweder ein API-Token oder Benutzername und Kennwort."
      actions={<SourceLine source={g.source} updatedAt={g.updatedAt} />}>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <Stack gap={2}>
            <PasswordInput label="API-Token" autoComplete="new-password" disabled={clear.token}
              placeholder={g.tokenSet ? 'gesetzt – leer lassen zum Behalten' : 'nicht gesetzt'} value={token} onChange={(e) => setToken(e.currentTarget.value)} />
            {g.tokenSet && <Checkbox size="xs" label="Token entfernen" checked={clear.token} onChange={(e) => setClear((c) => ({ ...c, token: e.currentTarget.checked }))} />}
          </Stack>
          <TextInput label="oder Benutzername" autoComplete="off" value={user} onChange={(e) => setUser(e.currentTarget.value)} />
          <Stack gap={2}>
            <PasswordInput label="Kennwort" autoComplete="new-password" disabled={clear.password}
              placeholder={g.passwordSet ? 'gesetzt – leer lassen zum Behalten' : 'nicht gesetzt'} value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
            {g.passwordSet && <Checkbox size="xs" label="Kennwort entfernen" checked={clear.password} onChange={(e) => setClear((c) => ({ ...c, password: e.currentTarget.checked }))} />}
          </Stack>
        </SimpleGrid>
        {save.isError && <Alert color="red" py={6}>{message(save.error)}</Alert>}
        {reset.isError && <Alert color="red" py={6}>{message(reset.error)}</Alert>}
        <TestResult result={test.data} error={test.error} />
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate()} loading={save.isPending}>Speichern</Button>
          <Button size="xs" variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={g.source === 'none'} title="meldet sich einmal bei GENESIS-Online an und ab">Anmeldung prüfen</Button>
          {g.source === 'pst' && <Button size="xs" variant="subtle" color="gray" onClick={() => reset.mutate()} loading={reset.isPending}>Auf .env zurücksetzen</Button>}
        </Group>
      </Stack>
    </Section>
  );
}

function AnthropicSection({ view }: { view: ConnectionsView }): JSX.Element {
  const a = view.anthropic;
  const [key, setKey] = useState('');
  const { refresh, test, reset } = useConnectionActions('anthropic');
  const save = useMutation({
    mutationFn: (value: string) => connectionsApi.saveAnthropic(value),
    onSuccess: () => setKey(''),
    onSettled: refresh,
  });
  return (
    <Section title="Claude-API" description="Schlüssel für die KI-Auswertung über die Claude-API (ADR 0022). Welcher Weg gilt, steht unter Einstellungen › KI-Auswertung. Die Nutzung wird über das Konto bei Anthropic abgerechnet; ein Ausgabenlimit setzen Sie in der Anthropic-Console."
      actions={<SourceLine source={a.source} updatedAt={a.updatedAt} />}>
      <Stack gap="sm">
        <PasswordInput label="API-Schlüssel" autoComplete="new-password" maw={420}
          placeholder={a.keySet ? 'gesetzt – zum Ersetzen neuen eingeben' : 'sk-ant-…'} value={key} onChange={(e) => setKey(e.currentTarget.value)} />
        {save.isError && <Alert color="red" py={6}>{message(save.error)}</Alert>}
        {reset.isError && <Alert color="red" py={6}>{message(reset.error)}</Alert>}
        <TestResult result={test.data} error={test.error} />
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate(key.trim())} loading={save.isPending} disabled={!key.trim()}>Speichern</Button>
          <Button size="xs" variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={!a.keySet} title="fragt bei der Claude-API das eingestellte Modell ab; verbraucht keine Token">Verbindung prüfen</Button>
          {a.source === 'pst' && <Button size="xs" variant="subtle" color="gray" onClick={() => reset.mutate()} loading={reset.isPending} title="entfernt den in PST gespeicherten Schlüssel; steht einer in der .env, gilt danach dieser">In PST entfernen</Button>}
        </Group>
      </Stack>
    </Section>
  );
}

/** Where a portal stands: stopped after a warning sign, and the test login asked for on saving. */
function PortalState({ portal }: { portal: PortalConnection }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const release = useMutation({
    mutationFn: () => connectionsApi.releasePortal(portal.id, note.trim()),
    onSuccess: () => setNote(''),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['connections'] }),
  });
  return (
    <Stack gap="xs">
      {portal.stoppedAt && (
        <Alert color="red" py={6} title={`Gestoppt seit ${formatDateTime(portal.stoppedAt)}`}>
          <Stack gap="xs">
            <Text size="sm">{portal.stoppedReason ?? 'ohne Angabe'}. Bis zur Freigabe laufen keine Abzüge und keine Einzelabfragen.</Text>
            <Group gap="xs" align="flex-end">
              <TextInput size="xs" label="Vermerk zur Freigabe" placeholder="z. B. im Browser geprüft, Konto nicht gesperrt" value={note} onChange={(e) => setNote(e.currentTarget.value)} style={{ flex: 1 }} />
              <Button size="xs" color="red" variant="light" disabled={note.trim().length < 3} loading={release.isPending} onClick={() => release.mutate()}>Wieder freigeben</Button>
            </Group>
            {release.isError && <Text size="xs" c="red">{message(release.error)}</Text>}
          </Stack>
        </Alert>
      )}
      {portal.probeAt ? (
        <Alert color={portal.probeOk ? 'green' : 'red'} py={6}>
          Probeanmeldung {formatDateTime(portal.probeAt)}: {portal.probeMessage ?? (portal.probeOk ? 'erfolgreich' : 'fehlgeschlagen')}
        </Alert>
      ) : portal.probeRequestedAt ? (
        <Alert color="gray" py={6}>
          Probeanmeldung angefordert {formatDateTime(portal.probeRequestedAt)} – der Abzugsdienst am Server nimmt sie in der Regel innerhalb von 1–2 Minuten (Stand unter Einstellungen › Abzüge).
        </Alert>
      ) : null}
    </Stack>
  );
}

function PortalSection({ portal }: { portal: PortalConnection }): JSX.Element {
  const info = FETCH_PORTALS[portal.id];
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['connections'] });
  const [user, setUser] = useState(portal.user);
  const [password, setPassword] = useState('');
  const [enabled, setEnabled] = useState(portal.enabled);
  const [throttle, setThrottle] = useState<PortalThrottle>(portal.throttle);
  const [showThrottle, setShowThrottle] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const save = useMutation({
    mutationFn: () => connectionsApi.savePortal(portal.id, { user: user.trim(), enabled, throttle, ...(password ? { password } : {}) }),
    onSuccess: () => setPassword(''),
    onSettled: refresh,
  });
  const probe = useMutation({ mutationFn: () => connectionsApi.probePortal(portal.id), onSettled: refresh });
  const remove = useMutation({ mutationFn: () => connectionsApi.removePortal(portal.id), onSettled: refresh });
  const set = (key: keyof typeof PORTAL_THROTTLE_BOUNDS) => (v: number | string): void => setThrottle((t) => ({ ...t, [key]: Number(v) || 0 }));
  const status = portal.stoppedAt ? { label: 'gestoppt', color: 'red' } : !portal.passwordSet ? { label: 'nicht eingerichtet', color: 'orange' } : portal.enabled ? { label: PORTAL_ALLOW.on, color: 'green' } : { label: PORTAL_ALLOW.off, color: 'gray' };
  const jobs = useQuery({ queryKey: ['portal-jobs'], queryFn: portalJobsApi.overview, refetchInterval: 30_000 });
  const round = jobs.data?.rounds.find((r) => r.portal === portal.id);
  const recommended = PORTAL_THROTTLE_RECOMMENDED[portal.id];
  // Shown as a suggestion until the first chunk; the saved values stay until someone takes it over and saves.
  const suggest = !!round?.nightEnabled && !round.ramp.startedAt && (Object.keys(recommended) as (keyof PortalThrottle)[]).some((k) => portal.throttle[k] !== recommended[k]);
  return (
    <Section title={info.label}
      description={<>Zugang des Büros für automatische Abzüge ({info.what}; ADR 0023). Oberste Regel ist, dass das Konto nicht gesperrt wird: PST ruft langsam und mit Tageslimit ab und hält bei jedem Warnzeichen an. Beim Speichern meldet sich PST einmal zur Probe an.</>}
      actions={<Group gap="xs"><Badge variant="light" color={status.color}>{status.label}</Badge>{portal.updatedAt && <Text size="xs" c="dimmed">geändert {formatDateTime(portal.updatedAt)}</Text>}</Group>}>
      <Stack gap="sm">
        <PortalState portal={portal} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Benutzername" autoComplete="off" value={user} onChange={(e) => setUser(e.currentTarget.value)} />
          <PasswordInput label="Kennwort" autoComplete="new-password"
            placeholder={portal.passwordSet ? 'gesetzt – leer lassen zum Behalten' : 'nicht gesetzt'} value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
        </SimpleGrid>
        <Checkbox label={PORTAL_ALLOW.label} description={PORTAL_ALLOW.hint} checked={enabled} onChange={(e) => setEnabled(e.currentTarget.checked)} />
        {round && portal.passwordSet && <PortalRound round={round} />}
        {suggest && (
          <Alert color="blue" py={6} title={PORTAL_NIGHT.recommend}>
            <Stack gap={4}>
              <Text size="xs">
                {recommended.dailyLimit} Seitenaufrufe je Tag, Pause {recommended.pauseMinSeconds}–{recommended.pauseMaxSeconds} s, nach {recommended.burstEvery} Aufrufen {recommended.burstPauseMinutes} min,
                nach Download {recommended.downloadPauseSeconds} s, Lauf höchstens {recommended.runMinutes} min, nachts {recommended.windowStart}–{recommended.windowEnd} Uhr.
                {portal.id === 'heinze' ? ' Bei heinze.de bewusst am kleinsten (geschätzt, nicht erprobt).' : ''} Gespeichert wird erst mit „Speichern“.
              </Text>
              <Group gap="xs">
                <Button size="xs" variant="light" onClick={() => { setThrottle({ ...recommended }); setShowThrottle(true); }}>{PORTAL_NIGHT.recommendApply}</Button>
              </Group>
            </Stack>
          </Alert>
        )}
        <UnstyledButton onClick={() => setShowThrottle((v) => !v)}>
          <Text size="sm" c="dimmed">{showThrottle ? '▾' : '▸'} Drosselung: {throttle.dailyLimit} Seitenaufrufe je Tag, Pause {throttle.pauseMinSeconds}–{throttle.pauseMaxSeconds} s, nachts {throttle.windowStart}–{throttle.windowEnd} Uhr</Text>
        </UnstyledButton>
        <Collapse in={showThrottle}>
          <Stack gap="xs">
            <SimpleGrid cols={{ base: 1, xs: 2, sm: 4 }}>
              {(Object.keys(PORTAL_THROTTLE_FIELDS) as (keyof typeof PORTAL_THROTTLE_FIELDS)[]).map((key) => (
                <NumberInput key={key} size="xs" label={PORTAL_THROTTLE_FIELDS[key].label} title={PORTAL_THROTTLE_FIELDS[key].hint}
                  min={PORTAL_THROTTLE_BOUNDS[key].min} max={PORTAL_THROTTLE_BOUNDS[key].max} allowDecimal={false} value={throttle[key]} onChange={set(key)}
                  description={`${PORTAL_THROTTLE_BOUNDS[key].min}–${PORTAL_THROTTLE_BOUNDS[key].max}, Vorgabe ${PORTAL_THROTTLE_DEFAULTS[key]}`} />
              ))}
              <TextInput size="xs" label="Zeitfenster von" description={`Vorgabe ${PORTAL_THROTTLE_DEFAULTS.windowStart}`} value={throttle.windowStart} onChange={(e) => setThrottle((t) => ({ ...t, windowStart: e.currentTarget.value }))} />
              <TextInput size="xs" label="Zeitfenster bis" description={`Vorgabe ${PORTAL_THROTTLE_DEFAULTS.windowEnd}`} value={throttle.windowEnd} onChange={(e) => setThrottle((t) => ({ ...t, windowEnd: e.currentTarget.value }))} />
            </SimpleGrid>
            <Group gap="xs">
              <Button size="xs" variant="subtle" color="gray" onClick={() => setThrottle({ ...PORTAL_THROTTLE_DEFAULTS })}>Vorgaben übernehmen</Button>
              <Text size="xs" c="dimmed">Höhere Werte machen eine Sperre wahrscheinlicher. Erst nach einer ruhigen Woche erhöhen.</Text>
            </Group>
          </Stack>
        </Collapse>
        {save.isError && <Alert color="red" py={6}>{message(save.error)}</Alert>}
        {probe.isError && <Alert color="red" py={6}>{message(probe.error)}</Alert>}
        {remove.isError && <Alert color="red" py={6}>{message(remove.error)}</Alert>}
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate()} loading={save.isPending} disabled={!user.trim() || (!portal.passwordSet && !password)}>Speichern</Button>
          <Button size="xs" variant="default" onClick={() => probe.mutate()} loading={probe.isPending} disabled={!portal.passwordSet} title="meldet sich einmal an und wieder ab; zählt gegen das Tageslimit">Probeanmeldung</Button>
          {portal.passwordSet && (confirmRemove
            ? <Button size="xs" variant="light" color="red" onClick={() => remove.mutate(undefined, { onSettled: () => setConfirmRemove(false) })} loading={remove.isPending}>Wirklich entfernen?</Button>
            : <Button size="xs" variant="subtle" color="gray" onClick={() => setConfirmRemove(true)}>Zugang entfernen</Button>)}
          <Text size="xs" c="dimmed" component="a" href={info.url} target="_blank" rel="noreferrer">{info.label} öffnen ↗</Text>
        </Group>
      </Stack>
    </Section>
  );
}

function NtfySection({ view }: { view: ConnectionsView }): JSX.Element {
  const n = view.ntfy;
  const [url, setUrl] = useState(n.url);
  const [topic, setTopic] = useState(n.topic);
  const [user, setUser] = useState(n.user);
  const [password, setPassword] = useState('');
  const { refresh, test, reset } = useConnectionActions('ntfy');
  const save = useMutation({
    mutationFn: () => connectionsApi.saveNtfy({ url: url.trim(), topic: topic.trim(), user: user.trim(), ...(password ? { password } : {}) }),
    onSuccess: () => setPassword(''),
    onSettled: refresh,
  });
  return (
    <Section title="ntfy" description="Benachrichtigung aufs Telefon, wenn ein Portal-Abzug anhält – zusätzlich zur E-Mail an die Administration. Mit Benutzername gilt das Feld darunter als Kennwort, ohne Benutzername als Zugangstoken."
      actions={<SourceLine source={n.source} updatedAt={n.updatedAt} />}>
      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput label="Adresse des Servers" placeholder="https://ntfy.example.org" value={url} onChange={(e) => setUrl(e.currentTarget.value)} />
          <TextInput label="Thema" placeholder="pst-abzuege" value={topic} onChange={(e) => setTopic(e.currentTarget.value)} />
          <TextInput label="Benutzername (leer bei Token)" autoComplete="off" value={user} onChange={(e) => setUser(e.currentTarget.value)} />
          <PasswordInput label="Kennwort oder Token" autoComplete="new-password"
            placeholder={n.passwordSet ? 'gesetzt – leer lassen zum Behalten' : 'nicht gesetzt'} value={password} onChange={(e) => setPassword(e.currentTarget.value)} />
        </SimpleGrid>
        {save.isError && <Alert color="red" py={6}>{message(save.error)}</Alert>}
        {reset.isError && <Alert color="red" py={6}>{message(reset.error)}</Alert>}
        <TestResult result={test.data} error={test.error} />
        <Group gap="xs">
          <Button size="xs" onClick={() => save.mutate()} loading={save.isPending} disabled={!url.trim() || !topic.trim()}>Speichern</Button>
          <Button size="xs" variant="default" onClick={() => test.mutate()} loading={test.isPending} disabled={n.source === 'none'} title="schickt eine Testnachricht an das Thema">Testnachricht</Button>
          {n.source === 'pst' && <Button size="xs" variant="subtle" color="gray" onClick={() => reset.mutate()} loading={reset.isPending}>Entfernen</Button>}
        </Group>
      </Stack>
    </Section>
  );
}
